import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { 
  TrendingUp,
  Calendar,
  Download,
  BarChart3,
  PieChart as PieChartIcon,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('6months');
  const [analyticsType, setAnalyticsType] = useState('revenue');

  // Mock advanced analytics data
  const salesByCategory = [
    { category: 'Flower', sales: 285000, units: 12500, avgPrice: 22.8 },
    { category: 'Edibles', sales: 156000, units: 8200, avgPrice: 19.0 },
    { category: 'Concentrates', sales: 198000, units: 5400, avgPrice: 36.7 },
    { category: 'Pre-Rolls', sales: 89000, units: 6700, avgPrice: 13.3 },
    { category: 'Vapes', sales: 145000, units: 4200, avgPrice: 34.5 },
    { category: 'Tinctures', sales: 67000, units: 3100, avgPrice: 21.6 },
  ];

  const monthlyComparison = [
    { month: 'Jan', revenue2024: 385000, revenue2023: 320000, growth: 20.3 },
    { month: 'Feb', revenue2024: 415000, revenue2023: 345000, growth: 20.3 },
    { month: 'Mar', revenue2024: 445000, revenue2023: 370000, growth: 20.3 },
    { month: 'Apr', revenue2024: 475000, revenue2023: 390000, growth: 21.8 },
    { month: 'May', revenue2024: 495000, revenue2023: 410000, growth: 20.7 },
    { month: 'Jun', revenue2024: 525000, revenue2023: 425000, growth: 23.5 },
  ];

  const customerSegments = [
    { name: 'Retail Customers', value: 45, color: '#10b981' },
    { name: 'Medical Patients', value: 30, color: '#6366f1' },
    { name: 'Wholesale', value: 15, color: '#f59e0b' },
    { name: 'Online Orders', value: 10, color: '#ec4899' },
  ];

  const statePerformance = [
    { state: 'California', revenue: 425000, operators: 450, avgOrder: 945 },
    { state: 'Colorado', revenue: 385000, operators: 380, avgOrder: 1013 },
    { state: 'Oregon', revenue: 298000, operators: 295, avgOrder: 1010 },
    { state: 'Washington', revenue: 267000, operators: 268, avgOrder: 996 },
    { state: 'Nevada', revenue: 189000, operators: 175, avgOrder: 1080 },
  ];

  const complianceMetrics = [
    { metric: 'License Compliance', score: 95 },
    { metric: 'Inventory Accuracy', score: 92 },
    { metric: 'Reporting Timeliness', score: 88 },
    { metric: 'COA Completion', score: 90 },
    { metric: 'Safety Standards', score: 96 },
    { metric: 'Documentation', score: 85 },
  ];

  const inventoryTurnover = [
    { month: 'Jan', turnover: 4.2, daysOnHand: 21 },
    { month: 'Feb', turnover: 4.5, daysOnHand: 19 },
    { month: 'Mar', turnover: 4.8, daysOnHand: 18 },
    { month: 'Apr', turnover: 4.3, daysOnHand: 20 },
    { month: 'May', turnover: 4.6, daysOnHand: 19 },
    { month: 'Jun', turnover: 5.1, daysOnHand: 17 },
  ];

  const topProducts = [
    { product: 'Blue Dream', revenue: 45000, units: 2100, margin: 28 },
    { product: 'OG Kush Concentrate', revenue: 38000, units: 980, margin: 35 },
    { product: 'Sour Diesel Gummies', revenue: 32000, units: 1600, margin: 22 },
    { product: 'Purple Kush', revenue: 28000, units: 1250, margin: 30 },
    { product: 'CBD Tincture', revenue: 24000, units: 800, margin: 38 },
  ];

  const handleExport = () => {
    toast.success('Exporting analytics report...');
  };

  const summaryCards = [
    {
      title: 'Total Revenue',
      value: '$2.74M',
      change: '+23.5%',
      changeType: 'positive',
      period: 'vs last period'
    },
    {
      title: 'Avg Order Value',
      value: '$1,012',
      change: '+8.2%',
      changeType: 'positive',
      period: 'vs last period'
    },
    {
      title: 'Inventory Turnover',
      value: '4.6x',
      change: '+12.1%',
      changeType: 'positive',
      period: 'vs last period'
    },
    {
      title: 'Compliance Score',
      value: '91%',
      change: '+3.2%',
      changeType: 'positive',
      period: 'vs last period'
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Advanced Analytics</h1>
            <p className="text-muted-foreground mt-1">Comprehensive insights and performance metrics</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[180px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1month">Last Month</SelectItem>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="1year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div>
                  <p className="text-sm text-muted-foreground">{card.title}</p>
                  <p className="text-3xl font-bold mt-2">{card.value}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <TrendingUp className="h-3 w-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">{card.change}</span>
                    <span className="text-xs text-muted-foreground">{card.period}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Revenue Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Year-over-Year Revenue Comparison</CardTitle>
            <CardDescription>Monthly revenue comparison with growth rate</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={monthlyComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue2024" fill="#10b981" name="2024 Revenue" />
                <Bar yAxisId="left" dataKey="revenue2023" fill="#94a3b8" name="2023 Revenue" />
                <Line yAxisId="right" type="monotone" dataKey="growth" stroke="#6366f1" strokeWidth={2} name="Growth %" />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Sales by Category & Customer Segments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales by Category */}
          <Card>
            <CardHeader>
              <CardTitle>Sales by Product Category</CardTitle>
              <CardDescription>Revenue breakdown by category</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesByCategory} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" />
                  <YAxis dataKey="category" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="sales" fill="#10b981" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Customer Segments */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Segments</CardTitle>
              <CardDescription>Revenue distribution by customer type</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={customerSegments}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {customerSegments.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* State Performance */}
        <Card>
          <CardHeader>
            <CardTitle>State-by-State Performance</CardTitle>
            <CardDescription>Revenue and operator metrics by state</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statePerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="state" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#10b981" name="Revenue ($)" />
                <Bar dataKey="operators" fill="#6366f1" name="Operators" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Compliance Metrics & Inventory Turnover */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compliance Radar */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Metrics</CardTitle>
              <CardDescription>Multi-dimensional compliance scoring</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={complianceMetrics}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Inventory Turnover */}
          <Card>
            <CardHeader>
              <CardTitle>Inventory Turnover Rate</CardTitle>
              <CardDescription>Efficiency metrics over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={inventoryTurnover}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="turnover" stroke="#10b981" strokeWidth={2} name="Turnover Rate" />
                  <Line type="monotone" dataKey="daysOnHand" stroke="#f59e0b" strokeWidth={2} name="Days on Hand" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Top Products Table */}
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Products</CardTitle>
            <CardDescription>Best sellers by revenue and margin</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topProducts.map((product, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium">{product.product}</p>
                    <p className="text-sm text-muted-foreground">{product.units.toLocaleString()} units sold</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="font-bold">${product.revenue.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Margin</p>
                      <Badge variant="success">{product.margin}%</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
